import { fillPath } from "@Helpers/urlPath";
import { objectIdString } from "@Helpers/schemas/common.schema";

/**
 * ⚠️ KNOWN GAP (see `EstimateUrlProps`): a path value the caller may not hold is written as "undefined" / "null" when
 * absent, so its link opens a broken page (for an id, the router's "Invalid URL" page). Callers that pass an optional
 * value get such a link today (getRecentAccessUrl.ts, Notification/helper.tsx, useFileManager.tsx,
 * FormsAndFields/Forms/index.tsx). Fix at each caller: narrow first, then make the parameter required and drop this.
 */
export const absentAsText = (value: string | null | undefined): string => value ?? String(value);

/** The query every project page carries: Router.tsx loads the project its `projectId` names before rendering. */
const projectQuery = (projectId: string) => new URLSearchParams({ projectId });

export const getProjectWorkspacesListPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/Workspaces", { projectId })}?${projectQuery(projectId).toString()}`;

// ⚠️ Unused (no caller), and routes.tsx has no `riskManagement` route: a dead-code candidate.
export const getProjectRiskManagementListPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/riskManagement", { projectId })}?${projectQuery(projectId).toString()}`;

// Project-level "Folders" page — the multi-space file tree (all Spaces of the project as roots).
export const getProjectFoldersPageUrl = (
  projectId: string,
  spaceId?: string,
  path: string[] = [],
  searchText: string = "",
) => {
  const searchParams = new URLSearchParams();
  searchParams.set("projectId", projectId);
  if (searchText) {
    searchParams.set("searchText", searchText);
  }
  if (spaceId && objectIdString.safeParse(spaceId).success) {
    searchParams.set("spaceId", spaceId);
  }
  let url = `${fillPath("/project/:projectId/folders", { projectId })}?${searchParams.toString()}`;

  const filteredPath = path.filter((val) => val && val != "0");
  if (filteredPath.length) {
    // FileExplorer reads the fragment back with decodeURIComponent, as fillPath encodes it.
    url += `#${fillPath("folderPath=:folderPath", { folderPath: `/${filteredPath.join("/")}/` })}`;
  }

  return url;
};

export const getProjectIssuesPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/issues", { projectId })}?${projectQuery(projectId).toString()}`;
export const getSingleIssuePageUrl = (projectId: string, issueId: string) => {
  const path = fillPath("/project/:projectId/issues/:issueId", { projectId, issueId });
  return `${path}?${projectQuery(projectId).toString()}`;
};
export const getProjectPhotosPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/photos", { projectId })}?${projectQuery(projectId).toString()}`;
export const getProjectFilesPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/listFiles", { projectId })}?${projectQuery(projectId).toString()}`;
export const getProjectTransmittalsPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/listTransmittals", { projectId })}?${projectQuery(projectId).toString()}`;
export { getProjectTasksPageUrl, getSingleTransmittalPageUrl } from "@Helpers/constants";
export const getProjectEstimatesPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/EstimateList", { projectId })}?${projectQuery(projectId).toString()}`;
export const getEstimatePageUrl = (projectId: string, estimateId: string) => {
  const path = fillPath("/project/:projectId/estimate/:estimateId", { projectId, estimateId });
  return `${path}?${projectQuery(projectId).toString()}`;
};
export const getProjectAssetsPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/assetsList", { projectId })}?${projectQuery(projectId).toString()}`;
export const getAssetRelatedDocumentsPageUrl = (projectId: string, assetId: string, assetTag: string) => {
  const path = fillPath("/project/:projectId/assetsList/:assetId/relatedDocuments", { projectId, assetId });
  return `${path}?${new URLSearchParams({ projectId, assetTag }).toString()}`;
};
export const getProjectSchedulesPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/scheduleList", { projectId })}?${projectQuery(projectId).toString()}`;
export const getProjectFormsPageUrl = (projectId: string, moduleName: string) => {
  const path = fillPath("/project/:projectId/formsList/:moduleName", {
    projectId,
    moduleName: moduleName.toLowerCase(),
  });
  return `${path}?${new URLSearchParams({ selectedTab: "list", projectId }).toString()}`;
};
export const getProjectDashboardPageUrl = (projectId: string | undefined) => {
  // ⚠️ KNOWN BUG: an absent id also reaches the query as "undefined", and Router.tsx then spins forever waiting for
  // a project it never loads. Fix: leave `projectId` out of the query when absent, as `getEstimateUrl` does; the
  // route guard then shows "Invalid URL".
  const id = absentAsText(projectId);
  const path = fillPath("/project/:projectId/projectdashboard", { projectId: id });
  return `${path}?${projectQuery(id).toString()}`;
};
export const getPortalProjectsUrl = (projectId: string) =>
  fillPath("/portalSettings/projects/:projectId/projectUsers", { projectId });
export const getUserProjects = (userId: string) => fillPath("/portalSettings/users/:userId/userProjects", { userId });
export const getProjectMailboxPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/mails", { projectId })}?${projectQuery(projectId).toString()}`;
export const getProjectReviewsPageUrl = (projectId: string) =>
  `/reviews?${new URLSearchParams({ projectId, type: "active" }).toString()}`;
export const getProjectContractPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/contractsList", { projectId })}?${projectQuery(projectId).toString()}`;
export const getReviewUrl = (projectId: string, spaceId: string, reviewId: string) =>
  `/review?${new URLSearchParams({ reviewId, spaceId, projectId }).toString()}`;
export const getProjectGeneralClashPageUrl = (projectId: string) =>
  `${fillPath("/project/:projectId/clashTests", { projectId })}?${projectQuery(projectId).toString()}`;
export const getProjectTemplatesUrl = (projectTemplateId?: string) => {
  if (projectTemplateId) {
    return fillPath("/portalSettings/projectTemplates/:projectTemplateId", { projectTemplateId });
  } else {
    return `/portalSettings/projectTemplates`;
  }
};
export const getCostDatabasesUrl = () => "/portalSettings/costDatabases";
export const getClashDetailsPageUrl = (projectId: string, clashId: string) => {
  const path = fillPath("/project/:projectId/clashDetails/:clashTestId", { projectId, clashTestId: clashId });
  return `${path}?${projectQuery(projectId).toString()}`;
};

/** `?projectId=…` and then the pairs of `extraSearchParams`, itself a query string ("a=1&b=2"), encoded like ours. */
const taskPageQuery = (projectId: string, extraSearchParams: string | undefined) => {
  const query = projectQuery(projectId);
  for (const [name, value] of new URLSearchParams(extraSearchParams)) {
    query.append(name, value);
  }
  return query;
};

// ⚠️ Unused (no caller), and routes.tsx has no `createTasks` / `editTask` route: dead-code candidates.
export const getCreateTaskPageUrl = (projectId: string, extraSearchParams?: string) => {
  const path = fillPath("/project/:projectId/createTasks", { projectId });
  return `${path}?${taskPageQuery(projectId, extraSearchParams).toString()}`;
};
export const getEditTaskPageUrl = (projectId: string, taskId: string, extraSearchParams?: string) => {
  const path = fillPath("/project/:projectId/editTask/:taskId", { projectId, taskId });
  return `${path}?${taskPageQuery(projectId, extraSearchParams).toString()}`;
};

/**
 * Every id is optional because callers pass what they hold: route params (`useParams()` values are
 * `string | undefined`), the file manager's `projectId` (absent when it has no project) and recent-access
 * metadata (absent in a broken entry). A builder cannot report a missing `projectId`, so all five below
 * leave it out of the query string, and a caller that can tell a broken record must check first.
 * ⚠️ KNOWN GAP: the ones that also put it in the PATH still write `"undefined"` there. Fix: take
 * `projectId: string` and have each caller narrow its value first.
 */
type EstimateUrlProps = {
  projectId?: string | undefined;
  estimateList?: boolean;
  estimateId?: string | undefined;
  versionId?: string | undefined;
};

export const getEstimateUrl = (props: EstimateUrlProps) => {
  const { estimateList, projectId, estimateId, versionId } = props;
  const searchParams = new URLSearchParams("");
  let location = fillPath("/project/:projectId", { projectId: absentAsText(projectId) });
  if (estimateList) {
    location += "/EstimateList";
  }
  if (estimateId) {
    location += fillPath("/estimate/:estimateId", { estimateId });

    if (versionId) {
      location += fillPath("/version/:versionId", { versionId });
    }
  }

  // Absent in the cases listed on `EstimateUrlProps`; left out rather than sent as "undefined".
  if (projectId) {
    searchParams.set("projectId", projectId);
  }

  return `${location}?${searchParams.toString()}`;
};

// Every key admits an explicit `undefined`: callers hand these straight off a recent-access
// metadata record, where each member is present-but-undefined for the modules that do not set it.
type ScheduleUrlProps = {
  projectId?: string | undefined;
  scheduleId?: string | undefined;
  selectedTab?: string | undefined;
};

export const getScheduleUrl = (props: ScheduleUrlProps) => {
  const { projectId, scheduleId, selectedTab = "Gantt" } = props;
  const searchParams = new URLSearchParams("");
  const location = fillPath("/project/:projectId/schedule/:scheduleId/scheduleDetails", {
    projectId: absentAsText(projectId),
    scheduleId: absentAsText(scheduleId),
  });

  searchParams.set("selectedTab", selectedTab);
  // Absent in the cases listed on `EstimateUrlProps`; left out rather than sent as "undefined".
  if (projectId) {
    searchParams.set("projectId", projectId);
  }

  return `${location}?${searchParams.toString()}`;
};

/** One saved version of a schedule, the page its "Preview" opens. */
export const getScheduleVersionPageUrl = (
  projectId: string,
  scheduleId: string,
  versionId: string,
  selectedTab = "Gantt",
) => {
  const path = fillPath("/project/:projectId/schedule/:scheduleId/version/:versionId", {
    projectId,
    scheduleId,
    versionId,
  });
  return `${path}?${new URLSearchParams({ selectedTab, projectId }).toString()}`;
};

export const getContractListUrl = (url: string, props: { projectId?: string; selectedTab?: string }) => {
  const { projectId, selectedTab } = props;
  const searchParams = new URLSearchParams("");

  if (projectId) {
    searchParams.set("projectId", projectId);
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }

  return `${url}?${searchParams.toString()}`;
};

export const getProjectSettingsPageUrl = ({
  projectId,
  tab,
  extraSearchParams = {},
}: {
  projectId: string;
  tab: string;
  extraSearchParams?: Record<string, string>;
}) => {
  const params = new URLSearchParams({
    projectId,
    tab,
    ...extraSearchParams,
  });
  return `${fillPath("/project/:projectId/setting", { projectId })}?${params.toString()}`;
};

type ContractDetailUrlProps = {
  projectId?: string | undefined;
  /** `null` when a form instance is not linked to a contract. */
  contractId?: string | null | undefined;
  selectedTab?: string | undefined;
  /**
   * Callers hand this in as `ObjectIdString | null` (route/instance data) and, from the contract
   * version grid, as `string | boolean` — that row type declares `versionId?: string | boolean`.
   * Widened to what the callers actually pass; only a truthy value ever reaches the query string.
   */
  versionId?: string | boolean | null | undefined;
};

export const getContractDetailUrl = (props: ContractDetailUrlProps) => {
  const { selectedTab, projectId, contractId, versionId } = props;

  const url = fillPath("/project/:projectId/contract/:contractId", {
    projectId: absentAsText(projectId),
    contractId: absentAsText(contractId),
  });
  const searchParams = new URLSearchParams("");

  if (projectId) {
    searchParams.set("projectId", projectId);
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }
  if (versionId) {
    // Truthy here, so never `false`/`null`; the grid's `boolean` variant is a row-type artefact, and
    // `String` on the remaining `string | true` leaves a real id untouched.
    searchParams.set("versionId", String(versionId));
  }

  return `${url}?${searchParams.toString()}`;
};

export const getFormsListUrl = (props: { projectId?: string; selectedTab?: string; resourceType?: string }) => {
  const { selectedTab, projectId, resourceType } = props;

  const url = fillPath("/project/:projectId/formsList/:moduleName", {
    projectId: absentAsText(projectId),
    moduleName: absentAsText(resourceType?.toLowerCase()),
  });
  const searchParams = new URLSearchParams("");

  if (projectId) {
    searchParams.set("projectId", projectId);
  }
  if (selectedTab) {
    searchParams.set("selectedTab", selectedTab);
  }

  return `${url}?${searchParams.toString()}`;
};

/**
 * `searchParams` is the search term: `null` when the current URL has none (`URLSearchParams.get`),
 * and the new URL then carries none either.
 */
export const getProjectPageUrl = (searchParams: string | null, typeProject: string, viewProject: string) => {
  const params = new URLSearchParams();

  if (searchParams) {
    params.set("search", searchParams);
  }

  if (typeProject) {
    params.set("type", typeProject);
  }
  if (viewProject) {
    params.set("view", viewProject);
  }

  return `/projects?${params.toString()}`;
};
